import type { CentralRiskEventBbox } from '../components/central-monitoring/centralRiskEventTypes';
import {
  DEFAULT_RISK_BBOX_COLOR,
  hexToRgba,
  resolveRiskAppearance,
} from '../constants/riskEventAppearance';
import { getBboxBorderColor } from './bboxOverlayColors';

/**
 * 팝업용 원본 프레임에 bbox를 그려 JPEG data URL로 반환.
 * 실패 시 원본을 그대로 반환한다.
 */
export async function composeEventSnapshotWithBboxes(
  imageDataUrl: string,
  bboxes?: CentralRiskEventBbox[] | null,
  opts?: { defaultRiskColor?: string | null }
): Promise<string> {
  const src = (imageDataUrl || '').trim();
  if (!src.startsWith('data:')) return src;
  if (!Array.isArray(bboxes) || bboxes.length === 0) return src;
  const defaultRisk = String(opts?.defaultRiskColor || '').trim() || DEFAULT_RISK_BBOX_COLOR;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx || !canvas.width || !canvas.height) {
          resolve(src);
          return;
        }
        ctx.drawImage(img, 0, 0);
        for (const track of bboxes) {
          if (!Array.isArray(track.bbox) || track.bbox.length < 4) continue;
          const fw = Math.max(1, Number(track.frame_width) || canvas.width);
          const fh = Math.max(1, Number(track.frame_height) || canvas.height);
          const sx = canvas.width / fw;
          const sy = canvas.height / fh;
          let [x1, y1, x2, y2] = track.bbox.map(Number);
          x1 *= sx;
          x2 *= sx;
          y1 *= sy;
          y2 *= sy;
          const left = Math.min(x1, x2);
          const top = Math.min(y1, y2);
          const w = Math.max(2, Math.abs(x2 - x1));
          const h = Math.max(2, Math.abs(y2 - y1));
          const atRisk = Boolean(track.motionRisk);
          const border = atRisk
            ? String(track.borderColor || '').trim() || defaultRisk
            : getBboxBorderColor(track.class_name || '');
          ctx.lineWidth = atRisk ? 3 : 2;
          ctx.strokeStyle = border;
          if (atRisk) {
            ctx.fillStyle = hexToRgba(border, 0.18);
            ctx.fillRect(left, top, w, h);
          }
          ctx.strokeRect(left, top, w, h);
          const label = formatBboxOverlayLabel(track);
          if (label) {
            ctx.font = 'bold 14px sans-serif';
            const tw = ctx.measureText(label).width + 8;
            const ly = Math.max(16, top - 4);
            ctx.fillStyle = atRisk ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.55)';
            ctx.fillRect(left, ly - 14, tw, 16);
            ctx.fillStyle = atRisk ? border : '#eee';
            ctx.fillText(label, left + 4, ly - 2);
          }
        }
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch {
        resolve(src);
      }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

export function dataUrlToBase64(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}

/** /api/... 캡처 등 인증이 필요한 이미지를 data URL로 로드 (img 태그 직접 표시용) */
export async function fetchAuthenticatedImageDataUrl(
  url: string
): Promise<string | null> {
  const src = (url || '').trim();
  if (!src) return null;
  if (src.startsWith('data:')) return src;
  try {
    const { getBearerTokenForApi } = await import('./poolSiteAuth');
    const token = getBearerTokenForApi();
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(src, {
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** bbox 라벨 텍스트 (코드명 — 설명) */
export function formatBboxOverlayLabel(
  track: Pick<CentralRiskEventBbox, 'riskSubtypeLabel' | 'riskDescription' | 'class_name' | 'motionRisk'>
): string {
  const code = String(track.riskSubtypeLabel || '').trim();
  const desc = String(track.riskDescription || '').trim();
  if (code && desc) {
    const short = desc.length > 24 ? `${desc.slice(0, 24)}…` : desc;
    return `${code} — ${short}`;
  }
  if (code) return code;
  if (desc) return desc;
  return track.class_name || (track.motionRisk ? '위험' : '');
}

/** 알림/저장용 — 각 bbox 고유 색·라벨 유지. 비어 있을 때만 기본값 채움 */
export function applyRiskAppearanceToBboxes(
  bboxes: CentralRiskEventBbox[] | undefined | null,
  riskType: string,
  overrides?: {
    label?: string | null;
    color?: string | null;
    /** true면 모든 위험 bbox에 동일 색/라벨 강제 (일괄 적용용) */
    forceAll?: boolean;
  }
): CentralRiskEventBbox[] {
  const app = resolveRiskAppearance(riskType, {
    label: overrides?.label,
    color: overrides?.color,
  });
  const labelForce = String(overrides?.label || '').trim();
  const colorForce = String(overrides?.color || '').trim();
  const forceAll = Boolean(overrides?.forceAll);
  const list = Array.isArray(bboxes) ? bboxes : [];
  return list.map((b) => {
    if (!b.motionRisk) return b;
    const ownLabel = String(b.riskSubtypeLabel || '').trim();
    const ownColor = String(b.borderColor || '').trim();
    return {
      ...b,
      riskSubtypeLabel: forceAll
        ? labelForce || app.label
        : ownLabel || labelForce || app.label,
      borderColor: forceAll
        ? colorForce || app.color
        : ownColor || colorForce || app.color,
    };
  });
}
